import Box from '@mui/material/Box';
import AnalyticsToolbar from 'components/@home/@analytics/AnalyticsToolbar';
import Loading from 'components/common/Loading';
import deferRender from 'defer-render-hoc';
import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import companyShape from 'shapes/company';
import { useListTeamsQuery } from 'store/app/entities/TeamsSlice';
import employeesStatus from 'store/selectors/employeesStatus';
import { useCompanyFeature } from 'utils/useCompanyFeature';
import useWindowSize from 'utils/useWindowSize';
import connector from './connector';

const ActivationPerTeam = React.lazy(() => import('./ActivationPerTeam'));
const ActivationTotal = React.lazy(() => import('./ActivationTotal'));
const ActiveUsers = React.lazy(() => import('./ActiveUsers'));
const MessageCounts = React.lazy(() => import('./MessageCounts'));
const UserSessionsDaily = React.lazy(() => import('./UserSessionsDaily'));
const UsersPerPlatform = React.lazy(() => import('./UsersPerPlatform'));
const AdvancedAnalyticsScene = React.lazy(() => import('./AdvancedAnalyticsScene'));

const loadingSx = {
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const AnalyticsScene = ({ authCompany }) => {
  const isAdvancedAnalyticsEnabled = useCompanyFeature('advancedAnalytics');
  const areUserAnalyticsEnabled =
    useCompanyFeature('userAnalytics') || window?.location?.hostname?.match(/staging/);
  const windowSize = useWindowSize();

  const width = windowSize.width > 800 ? (windowSize.width - 260) * 0.6 : windowSize.width - 100;
  const height = windowSize.width > 800 ? width / 2 : width;
  const { data } = useListTeamsQuery({ perPage: 1 });
  const { isLoading } = useSelector(employeesStatus);

  const ActivationComponent =
    !isLoading && (!data || data.totalCount < 2) ? ActivationTotal : ActivationPerTeam;

  if (isAdvancedAnalyticsEnabled) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'secondary.ultraLight',
        }}
      >
        <Suspense fallback={<Loading sx={loadingSx} />}>
          <AdvancedAnalyticsScene />
        </Suspense>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      <AnalyticsToolbar />
      <Suspense fallback={<Loading sx={loadingSx} />}>
        {!isLoading && (
          <ActivationComponent
            company={authCompany}
            keys={['active', 'pending', 'blocked']}
            width={width}
            height={height}
          />
        )}
        <ActiveUsers width={width} height={height} />
        <MessageCounts width={width} height={height} />
        {!areUserAnalyticsEnabled && (
          <>
            <UserSessionsDaily width={width} height={height} />
            <UsersPerPlatform width={width} height={height} />
          </>
        )}
      </Suspense>
    </Box>
  );
};

AnalyticsScene.propTypes = {
  authCompany: companyShape.isRequired,
};

export default deferRender(connector(AnalyticsScene));
